import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import FileViewerModal from '../common/FileViewerModal';
import UnifiedViewTab from './UnifiedViewTab';
import SubInvoicesTab from './SubInvoicesTab';

interface InvoiceData {
  itinerary_id: string;
  lead_guest_name: string;
  travel_date: string;
  agency_name: string;
  total_amount: number;
  discount: number;
  subtotal_amount: number;
  amount_paid: number;
  balance_amount: number;
  invoice_status: string;
  payment_status: string;
  converted_date: string;
  booking_id: number;
  trip_status: string;
  quotation_number?: string;
  version_number?: number;
  total_paid_all_versions?: number;
}

interface InvoiceItem {
  id?: number;
  description: string;
  qty: number;
  rate: number;
  amount: number;
}

interface Payment {
  id?: number;
  date: string;
  amount: number;
  currency: string;
  exchange_rate: number;
  account: string;
  remarks: string;
  proof_url?: string;
  proof_verified?: boolean;
  proof_file?: File;
  // FX conversion fields
  payment_currency?: string;
  invoice_currency_equivalent?: number;
  xe_rate?: number;
  applied_rate?: number;
  rate_markup?: number;
  payment_type?: string;  // 'DIRECT' or 'GST'
}

interface PaymentSummary {
  set_price?: number;
  invoice_total: number;
  payments_total: number;
  balance_due: number;
  payment_status: string;
  passenger_info?: {
    total_pax: number;
    no_of_children: number;
    children_ages: number[];
  };
  invoice_needs_review?: boolean;
  invoice_created_for_itinerary?: number;
  current_itinerary_id?: number;
}

interface InvoiceRevampModalProps {
  invoice: InvoiceData;
  onClose: () => void;
  initialTab?: 'invoice' | 'payments' | 'gst' | 'sub_invoices' | 'unified' | 'reconciliation';
}

const CURRENCY_OPTIONS = ["INR", "USD", "IDR", "AUD"];
const PAYMENT_METHOD_OPTIONS = [
  "Grace - IDBI",
  "Wynder - Fed", 
  "Co-Operative Bank - ICICI",
  "Biji Mathew - SBI Pampady(Current)",
  "IIW - IDR",
  "IIW - USD",
  "Cash in Hand"
];

const BANK_OPTIONS = [
  "No Bank Details",
  "INR - Wynder India",
  "USD - Wynder Indonesia", 
  "IDR - Wynder Indonesia"
];

const InvoiceRevampModal: React.FC<InvoiceRevampModalProps> = ({ invoice, onClose, initialTab = 'invoice' }) => {
  const [activeTab, setActiveTab] = useState<'invoice' | 'payments' | 'gst' | 'sub_invoices' | 'unified' | 'reconciliation'>(initialTab);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [discount, setDiscount] = useState(0);
  const [selectedBank, setSelectedBank] = useState("No Bank Details");
  const [currency, setCurrency] = useState("IDR");
  const [saving, setSaving] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedPayments, setExpandedPayments] = useState<Set<number>>(new Set());
  const [uploading, setUploading] = useState<{ [key: number]: boolean }>({});
  const [viewerState, setViewerState] = useState<{ isOpen: boolean; fileUrl: string; fileName: string; fileType: 'pdf' | 'image' }>({ 
    isOpen: false, 
    fileUrl: '', 
    fileName: '', 
    fileType: 'image' 
  });
  const [sortField, setSortField] = useState<keyof InvoiceItem | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [inrRateInfo, setInrRateInfo] = useState<{ xe_rate: number; markup: number; applied_rate: number; rate_date?: string } | null>(null);
  const [loadingInrRate, setLoadingInrRate] = useState(false);
  const [rateOverrideIndex, setRateOverrideIndex] = useState<number | null>(null);
  const [rateOverrideValue, setRateOverrideValue] = useState('');

  // GST Invoice state
  const [gstData, setGstData] = useState<any>(null);
  const [gstExisting, setGstExisting] = useState<any>(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstSaving, setGstSaving] = useState(false);
  const [gstHandlingCharge, setGstHandlingCharge] = useState<number | null>(null);
  const [gstAdditionalItems, setGstAdditionalItems] = useState<{description: string; amount: number}[]>([]);
  const [gstDiscount, setGstDiscount] = useState(0);
  const [gstNotes, setGstNotes] = useState('');
  const [gstBookingDescription, setGstBookingDescription] = useState('Bali Package (Reimbursement to DMC)');
  const [gstBuyerGstin, setGstBuyerGstin] = useState('');
  const [gstBuyerState, setGstBuyerState] = useState('');
  const [gstBuyerStateCode, setGstBuyerStateCode] = useState('');
  const [gstInvoiceDate, setGstInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [gstRateOverride, setGstRateOverride] = useState<number | null>(null);
  const [gstHandlingOverride, setGstHandlingOverride] = useState<number | null>(null);
  const [gstBookingValueOverride, setGstBookingValueOverride] = useState<number | null>(null);
  const [gstDocumentType, setGstDocumentType] = useState<'PROFORMA' | 'TAX_INVOICE'>('PROFORMA');

  useEffect(() => {
    fetchInvoiceDetails();
  }, []);

  const fetchInvoiceDetails = async () => {
    try {
      const token = sessionStorage.getItem('jwt_token');
      const response = await fetch(`/api/invoice-revamp/${invoice.booking_id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      console.log('Fetched invoice details:', data);
      
      if (data.success) {
        console.log('Payments from backend:', data.data.payments);
        setInvoiceItems(data.data.items.length > 0 ? data.data.items : [
          { description: "", qty: 1, rate: 0, amount: 0 }
        ]);
        setPayments(data.data.payments.length > 0 ? data.data.payments : [
          { date: "", amount: 0, currency: "IDR", exchange_rate: 1.0, account: "", remarks: "" }
        ]);
        setDiscount(data.data.invoice.discount || 0);
        setCurrency(data.data.invoice.currency || "IDR");
        setPaymentSummary(data.data.payment_summary);
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    } finally {
      setLoading(false);
    }
  };

  const addInvoiceItem = () => {
    setInvoiceItems([...invoiceItems, { description: "", qty: 1, rate: 0, amount: 0 }]);
  };

  const removeInvoiceItem = (index: number) => {
    setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
  };

  const updateInvoiceItem = (index: number, field: keyof InvoiceItem, value: any) => {
    const updated = [...invoiceItems];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'qty' || field === 'rate') {
      updated[index].amount = updated[index].qty * updated[index].rate;
    }
    setInvoiceItems(updated);
  };

  const addPayment = () => {
    setPayments([...payments, { 
      date: new Date().toISOString().split('T')[0], 
      amount: 0, 
      currency: "IDR", 
      exchange_rate: 1.0, 
      account: "", 
      remarks: "" 
    }]);
  };

  const removePayment = async (index: number) => {
    const payment = payments[index];
    console.log('Attempting to delete payment:', { index, payment });
    
    // If it's an existing payment (has ID), delete from database immediately
    if (payment.id) {
      console.log(`Sending DELETE request to: /api/invoice-revamp/${invoice.booking_id}/payments/${payment.id}`);
      try {
        const token = sessionStorage.getItem('jwt_token');
        const response = await fetch(`/api/invoice-revamp/${invoice.booking_id}/payments/${payment.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log('Delete response status:', response.status);
        
        if (response.status === 404) {
          // Payment not found, just refresh data to sync UI with backend
          console.log('Payment not found (404), refreshing data');
          fetchInvoiceDetails();
          return;
        }
        
        const data = await response.json();
        if (data.success) {
          // Remove from local state
          setPayments(payments.filter((_, i) => i !== index));
          alert('Payment deleted successfully');
          fetchInvoiceDetails(); // Refresh data
        } else {
          alert('Error deleting payment: ' + data.message);
        }
      } catch (error) {
        console.error('Error deleting payment:', error);
        alert('Error deleting payment');
      }
    } else {
      // For new payments (no ID), just remove from local state
      console.log('Removing new payment (no ID) from local state');
      setPayments(payments.filter((_, i) => i !== index));
    }
  };

  const fetchInrRate = async (paymentDate?: string) => {
    setLoadingInrRate(true);
    try {
      const token = sessionStorage.getItem('jwt_token');
      const params = paymentDate ? `?date=${paymentDate}` : '';
      const response = await fetch(`/api/fx/inr-rate${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setInrRateInfo(data.data);
        return data.data;
      }
    } catch (error) {
      console.error('Error fetching INR rate:', error);
    } finally {
      setLoadingInrRate(false);
    }
    return null;
  };

  const loadGstData = async () => {
    setGstLoading(true);
    try {
      const token = sessionStorage.getItem('jwt_token');
      // Load existing GST invoice and calculate preview in parallel
      const [existingRes, calcRes] = await Promise.all([
        fetch(`/api/gst-invoice/${invoice.booking_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/gst-invoice/calculate?booking_id=${invoice.booking_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      const existingData = await existingRes.json();
      const calcData = await calcRes.json();

      if (existingData.success && existingData.data) {
        setGstExisting(existingData.data);
        setGstHandlingCharge(existingData.data.handling_charge || null);
        setGstAdditionalItems(existingData.data.additional_items || []);
        setGstDiscount(existingData.data.discount || 0);
        setGstNotes(existingData.data.notes || '');
        setGstBookingDescription(existingData.data.booking_description || 'Bali Package (Reimbursement to DMC)');
        setGstBuyerGstin(existingData.data.buyer_gstin || '');
        setGstBuyerState(existingData.data.buyer_state || '');
        setGstBuyerStateCode(existingData.data.buyer_state_code || '');
        setGstInvoiceDate(existingData.data.invoice_date || new Date().toISOString().split('T')[0]);
        setGstDocumentType(existingData.data.document_type || 'PROFORMA');
      }

      if (calcData.success) {
        const gst = calcData.data;
        // Use saved values if exists (supports override), otherwise use calculated
        if (existingData.success && existingData.data) {
          gst.booking_value_inr = existingData.data.booking_value_inr;
          gst.xe_rate = existingData.data.xe_rate;
          gst.applied_rate = existingData.data.applied_rate;
          gst.markup = existingData.data.applied_rate - existingData.data.xe_rate;
        } else if (currency === 'INR') {
          gst.booking_value_inr = paymentSummary?.invoice_total || invoice.total_amount || 0;
        }
        if (!gstHandlingCharge && gst.handling_charge) {
          setGstHandlingCharge(gst.handling_charge);
        }
        setGstData(gst);
        if (!existingData.data) {
          setGstBuyerGstin(gst.buyer?.gstin || '');
          setGstBuyerState(gst.buyer?.state || '');
          setGstBuyerStateCode(gst.buyer?.state_code || '');
        }
      }
    } catch (error) {
      console.error('Error loading GST data:', error);
    } finally {
      setGstLoading(false);
    }
  };

  const calculateHandlingCharge = (bookingValueInr: number): number => {
    if (bookingValueInr < 50000) return 500;
    if (bookingValueInr < 100000) return 1000;
    if (bookingValueInr < 150000) return 2000;
    return 3000;
  };

  const recalculateGst = async () => {
    try {
      const token = sessionStorage.getItem('jwt_token');
      const res = await fetch(`/api/gst-invoice/calculate?booking_id=${invoice.booking_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        const gst = data.data;
        if (currency === 'INR') {
          gst.booking_value_inr = paymentSummary?.invoice_total || invoice.total_amount || 0;
        }
        setGstHandlingCharge(gst.handling_charge);
        setGstData(gst);
      }
    } catch (error) {
      console.error('Error recalculating GST:', error);
    }
  };

  const saveGstInvoice = async () => {
    setGstSaving(true);
    try {
      const token = sessionStorage.getItem('jwt_token');
      const res = await fetch(`/api/gst-invoice/${invoice.booking_id}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          handling_charge: gstHandlingCharge,
          booking_value_inr_override: gstData?.booking_value_inr,
          xe_rate_override: gstData?.xe_rate,
          applied_rate_override: gstData?.applied_rate,
          additional_items: gstAdditionalItems,
          discount: gstDiscount,
          notes: gstNotes,
          booking_description: gstBookingDescription,
          buyer_gstin: gstBuyerGstin,
          buyer_state: gstBuyerState,
          buyer_state_code: gstBuyerStateCode,
          invoice_date: gstInvoiceDate,
          document_type: gstDocumentType,
        }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`GST Invoice ${data.gst_invoice_number} saved successfully`);
        loadGstData();
      } else {
        alert('Error: ' + (data.detail || data.message));
      }
    } catch (error) {
      console.error('Error saving GST invoice:', error);
      alert('Error saving GST invoice');
    } finally {
      setGstSaving(false);
    }
  };

  const applyInrRate = (index: number, rateInfo: any, currentPayments: Payment[]) => {
    const updatedWithRate = [...currentPayments];
    updatedWithRate[index] = {
      ...updatedWithRate[index],
      xe_rate: rateInfo.xe_rate,
      applied_rate: rateInfo.applied_rate,
      rate_markup: rateInfo.markup,
    };
    if (updatedWithRate[index].amount > 0) {
      updatedWithRate[index].invoice_currency_equivalent =
        Math.round((updatedWithRate[index].amount / rateInfo.applied_rate) * 100) / 100;
    }
    setPayments(updatedWithRate);
  };

  const updatePayment = (index: number, field: keyof Payment, value: any) => {
    const updated = [...payments];
    updated[index] = { ...updated[index], [field]: value };

    // When currency changes to INR, fetch rate for the payment date
    if (field === 'currency') {
      const exchangeRates: { [key: string]: number } = {
        'IDR': 1.0,
        'USD': 15000,
        'INR': 180,
        'AUD': 10000
      };
      updated[index].exchange_rate = exchangeRates[value] || 1.0;
      updated[index].payment_currency = value;

      if (value === 'INR' && currency === 'USD') {
        const payDate = updated[index].date || undefined;
        setPayments(updated);
        fetchInrRate(payDate).then(rateInfo => {
          if (rateInfo) applyInrRate(index, rateInfo, updated);
        });
        return;
      } else if (value === currency) {
        updated[index].xe_rate = 1;
        updated[index].applied_rate = 1;
        updated[index].rate_markup = 0;
        updated[index].invoice_currency_equivalent = updated[index].amount;
      } else {
        updated[index].xe_rate = undefined;
        updated[index].applied_rate = undefined;
        updated[index].rate_markup = undefined;
        updated[index].invoice_currency_equivalent = undefined;
      }
    }

    // When date changes and currency is INR, re-fetch historical rate
    if (field === 'date' && updated[index].payment_currency === 'INR' && currency === 'USD' && value) {
      setPayments(updated);
      fetchInrRate(value).then(rateInfo => {
        if (rateInfo) applyInrRate(index, rateInfo, updated);
      });
      return;
    }

    // When amount changes, recalculate invoice_currency_equivalent
    if (field === 'amount' && updated[index].payment_currency === 'INR' && updated[index].applied_rate) {
      updated[index].invoice_currency_equivalent =
        Math.round((value / updated[index].applied_rate!) * 100) / 100;
    } else if (field === 'amount' && updated[index].currency === currency) {
      updated[index].invoice_currency_equivalent = value;
    }

    setPayments(updated);
  };

  const uploadPaymentProof = async (paymentIndex: number, file: File) => {
    try {
      setUploading(prev => ({ ...prev, [paymentIndex]: true }));
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('payment_index', paymentIndex.toString());
      
      const token = sessionStorage.getItem('jwt_token');
      const response = await fetch(`/api/invoice-revamp/${invoice.booking_id}/upload-proof?payment_index=${paymentIndex}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      if (data.success) {
        // Update the payment with the proof URL
        const updatedPayments = [...payments];
        updatedPayments[paymentIndex] = {
          ...updatedPayments[paymentIndex],
          proof_url: data.proof_url
        };
        setPayments(updatedPayments);
        alert('Payment proof uploaded successfully');
      } else {
        alert('Error uploading payment proof: ' + data.message);
      }
    } catch (error) {
      console.error('Error uploading payment proof:', error);
      alert('Error uploading payment proof');
    } finally {
      setUploading(prev => ({ ...prev, [paymentIndex]: false }));
    }
  };

  const deletePaymentProof = async (paymentIndex: number) => {
    if (!window.confirm('Are you sure you want to delete this payment proof?')) return;
    
    try {
      const token = sessionStorage.getItem('jwt_token');
      const response = await fetch(`/api/invoice-revamp/${invoice.booking_id}/delete-proof/${paymentIndex}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        // Remove the proof URL from payment
        const updatedPayments = [...payments];
        updatedPayments[paymentIndex] = {
          ...updatedPayments[paymentIndex],
          proof_url: undefined
        };
        setPayments(updatedPayments);
        alert('Payment proof deleted successfully');
      } else {
        alert('Error deleting payment proof: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting payment proof:', error);
      alert('Error deleting payment proof');
    }
  };

  const verifyPaymentProof = async (paymentId: number, verified: boolean, notes: string = "") => {
    try {
      const token = sessionStorage.getItem('jwt_token');
      const response = await fetch(`/api/invoice-revamp/${paymentId}/verify-proof`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ verified, notes })
      });
      
      const data = await response.json();
      if (data.success) {
        alert('Payment proof verification updated');
        fetchInvoiceDetails(); // Refresh data
      }
    } catch (error) {
      console.error('Error verifying payment proof:', error);
      alert('Error verifying payment proof');
    }
  };

  const saveInvoice = async () => {
    // Validate items
    const validItems = invoiceItems.filter(item => item.description.trim() && item.amount > 0);
    if (validItems.length === 0) {
      alert('Please add at least one invoice item with description and amount.');
      return;
    }
    
    setSaving(true);
    try {
      const token = sessionStorage.getItem('jwt_token');
      const response = await fetch(`/api/invoice-revamp/${invoice.booking_id}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currency,
          items: invoiceItems,
          discount
        })
      });
      
      const data = await response.json();
      if (data.success) {
        alert('Invoice saved successfully');
        fetchInvoiceDetails(); // Refresh data
      } else {
        alert('Error saving invoice: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Error saving invoice');
    } finally {
      setSaving(false);
    }
  };

  const savePayments = async () => {
    // Validate mandatory fields
    const invalidPayments = payments.filter(payment => 
      !payment.date || 
      !payment.amount || 
      payment.amount <= 0 || 
      !payment.currency || 
      !payment.exchange_rate || 
      payment.exchange_rate <= 0 || 
      !payment.account
    );
    
    if (invalidPayments.length > 0) {
      alert('Please fill all mandatory fields (Payment Date, Amount, Currency, Exchange Rate, Payment Method) for all payments.');
      return;
    }
    
    // Validate IDR minimum amount (skip for ADDITIONAL payment type)
    const idrPayments = payments.filter(payment => payment.currency === 'IDR' && payment.amount < 100000 && payment.payment_type !== 'ADDITIONAL');
    
    if (idrPayments.length > 0) {
      alert('IDR payments must be at least 100,000. Please check your payment amounts.');
      return;
    }
    
    setSaving(true);
    try {
      const token = sessionStorage.getItem('jwt_token');
      
      // Add payment data
      const paymentData = payments.map(p => ({
        id: p.id,
        date: p.date,
        amount: p.amount,
        currency: p.currency,
        exchange_rate: p.exchange_rate,
        account: p.account,
        remarks: p.remarks,
        // FX conversion fields
        payment_currency: p.payment_currency || p.currency,
        invoice_currency_equivalent: p.invoice_currency_equivalent,
        xe_rate: p.xe_rate,
        applied_rate: p.applied_rate,
        rate_markup: p.rate_markup,
        payment_type: p.payment_type || 'DIRECT',
      }));
      
      const response = await fetch(`/api/invoice-revamp/${invoice.booking_id}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payments: paymentData
        })
      });
      
      const data = await response.json();
      if (data.success) {
        // Check if there are new payments that need proof upload
        const newPaymentsCount = payments.filter(p => !p.id).length;
        if (newPaymentsCount > 0) {
          alert(`Payments saved successfully! Please upload payment proofs for the ${newPaymentsCount} new payment(s).`);
        } else {
          alert('Payments saved successfully!');
        }
        fetchInvoiceDetails(); // Refresh data
      } else {
        alert('Error saving payments: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving payments:', error);
      alert('Error saving payments');
    } finally {
      setSaving(false);
    }
  };

  const printInvoice = async () => {
    try {
      const token = sessionStorage.getItem('jwt_token');
      const response = await fetch(`/api/invoice-revamp/${invoice.booking_id}/print?bank_details=${encodeURIComponent(selectedBank)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${invoice.itinerary_id}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error generating invoice');
      }
    } catch (error) {
      console.error('Error printing invoice:', error);
      alert('Error generating invoice');
    }
  };

  const handleSort = (field: keyof InvoiceItem) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedInvoiceItems = [...invoiceItems].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return 0;
  });

  const subtotal = invoiceItems.reduce((sum, item) => sum + item.amount, 0);
  const total = subtotal - discount;
  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const unpaid = Math.max(total - totalPaid, 0);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl h-full sm:h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 sm:p-6 border-b gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg sm:text-xl font-bold truncate">Invoice Management - {invoice.itinerary_id}</h2>
              {invoice.quotation_number && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-mono font-bold rounded-lg">
                  {invoice.quotation_number}/{invoice.version_number}
                </span>
              )}
            </div>
            <p className="text-sm sm:text-base text-gray-600 truncate">{invoice.lead_guest_name} | {invoice.agency_name}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button onClick={printInvoice} variant="secondary" size="sm" className="text-xs sm:text-sm">🖨️ Print</Button>
            <Button onClick={onClose} variant="secondary" size="sm" className="text-xs sm:text-sm">✕</Button>
          </div>
        </div>

        {/* Payment Summary Banner */}
        {paymentSummary && (
          <div className={`p-3 sm:p-4 ${
            paymentSummary.payment_status === 'FULLY_PAID' ? 'bg-green-50' : 
            paymentSummary.payment_status === 'PARTIALLY_PAID' ? 'bg-yellow-50' : 
            paymentSummary.payment_status === 'NOT_ISSUED' ? 'bg-gray-50' : 
            'bg-red-50'
          }`}>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 text-xs sm:text-sm mb-2">
              <div><strong>Set Price:</strong> {currency} {paymentSummary.set_price?.toLocaleString() || '0'}</div>
              <div><strong>Invoice Total:</strong> {currency} {paymentSummary.invoice_total.toLocaleString()}</div>
              <div><strong>Payments Total:</strong> {currency} {paymentSummary.payments_total.toLocaleString()}</div>
              <div><strong>Balance Due:</strong> {currency} {paymentSummary.balance_due.toLocaleString()}</div>
              <div><strong>Status:</strong> <span className={`font-semibold ${
                paymentSummary.payment_status === 'FULLY_PAID' ? 'text-green-600' : 
                paymentSummary.payment_status === 'PARTIALLY_PAID' ? 'text-yellow-600' : 
                paymentSummary.payment_status === 'NOT_ISSUED' ? 'text-gray-600' : 
                'text-red-600'
              }`}>{paymentSummary.payment_status}</span></div>
            </div>
            {invoice.quotation_number && invoice.total_paid_all_versions != null && invoice.total_paid_all_versions !== invoice.amount_paid && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mt-2">
                ⚠️ Total paid across all versions ({invoice.quotation_number}): <strong>{currency} {invoice.total_paid_all_versions?.toLocaleString()}</strong>
              </div>
            )}
            {paymentSummary.invoice_needs_review && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5 mt-2">
                🚨 <strong>Invoice review needed:</strong> Invoice was generated for WH_{paymentSummary.invoice_created_for_itinerary} but the current converted itinerary is WH_{paymentSummary.current_itinerary_id}. Please review and regenerate if needed.
              </div>
            )}
            {paymentSummary.passenger_info && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm pt-2 border-t border-gray-200">
                <div><strong>Total Pax:</strong> {paymentSummary.passenger_info.total_pax}</div>
                <div><strong>No of Kids:</strong> {paymentSummary.passenger_info.no_of_children}</div>
                <div><strong>Kids Ages:</strong> {paymentSummary.passenger_info.children_ages.length > 0 ? paymentSummary.passenger_info.children_ages.join(', ') : 'N/A'}</div>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'invoice' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('invoice')}
          >
            🧾 Invoice
          </button>
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'payments' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('payments')}
          >
            💳 Payments
          </button>
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'gst' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => { setActiveTab('gst'); if (!gstData) loadGstData(); }}
          >
            🇮🇳 GST Invoice
          </button>
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'sub_invoices' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('sub_invoices')}
          >
            📦 Additional
          </button>
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'unified' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('unified')}
          >
            📊 Unified View
          </button>
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'reconciliation' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('reconciliation')}
          >
            🔍 Reconciliation
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          {activeTab === 'invoice' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                <h3 className="text-base sm:text-lg font-semibold">Invoice Items</h3>
                <Button onClick={addInvoiceItem} size="sm" className="text-xs sm:text-sm">➕ Add Item</Button>
              </div>

              {/* Column Headers */}
              <div className="hidden sm:grid sm:grid-cols-12 gap-4 items-center p-3 bg-gray-50 rounded font-medium text-sm text-gray-700 mb-2">
                <button onClick={() => handleSort('description')} className="col-span-4 text-left hover:text-blue-600 flex items-center gap-1">
                  Description {sortField === 'description' && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
                <button onClick={() => handleSort('qty')} className="col-span-2 text-left hover:text-blue-600 flex items-center gap-1">
                  Qty {sortField === 'qty' && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
                <button onClick={() => handleSort('rate')} className="col-span-2 text-left hover:text-blue-600 flex items-center gap-1">
                  Rate {sortField === 'rate' && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
                <button onClick={() => handleSort('amount')} className="col-span-2 text-left hover:text-blue-600 flex items-center gap-1">
                  Amount {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
                <div className="col-span-2">Action</div>
              </div>

              <div className="space-y-4">
                {sortedInvoiceItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center p-3 sm:p-4 border rounded">
                    <div className="sm:col-span-4">
                      <label className="block text-xs font-medium text-gray-700 mb-1 sm:hidden">Description</label>
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateInvoiceItem(index, 'description', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1 sm:hidden">Qty</label>
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.qty}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateInvoiceItem(index, 'qty', parseInt(e.target.value) || 0)}
                        className="text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1 sm:hidden">Rate</label>
                      <Input
                        type="number"
                        placeholder="Rate"
                        value={item.rate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateInvoiceItem(index, 'rate', parseFloat(e.target.value) || 0)}
                        className="text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1 sm:hidden">Amount</label>
                      <div className="p-2 bg-gray-100 rounded text-right text-sm">
                        {item.amount.toLocaleString()}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Button onClick={() => removeInvoiceItem(index)} variant="secondary" size="sm" className="w-full sm:w-auto text-xs">
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Discount</label>
                  <Input
                    type="number"
                    value={discount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDiscount(parseFloat(e.target.value) || 0)}
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Bank Details</label>
                  <Select value={selectedBank} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedBank(e.target.value)} className="text-sm">
                    {BANK_OPTIONS.map(bank => (
                      <option key={bank} value={bank}>{bank}</option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-2 text-sm">
                  <div><strong>Subtotal:</strong> {subtotal.toLocaleString()}</div>
                  <div><strong>Discount:</strong> {discount.toLocaleString()}</div>
                  <div><strong>Total:</strong> {total.toLocaleString()}</div>
                </div>
              </div>

              <div className="mt-6">
                <Button onClick={saveInvoice} disabled={saving} className="text-sm">{saving ? 'Saving...' : '💾 Save Invoice'}</Button>
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-2">
                <h3 className="text-base sm:text-lg font-semibold">Customer Payments</h3>
                <Button onClick={addPayment} variant="primary" size="sm" className="text-xs sm:text-sm">
                  + Add Payment
                </Button>
              </div>

              {payments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm sm:text-base">No payments recorded yet.</p>
                  <Button onClick={addPayment} variant="primary" className="mt-4 text-xs sm:text-sm">
                    Add First Payment
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {payments.map((payment, index) => {
                    const isExpanded = expandedPayments.has(index);
                    const isExisting = payment.id !== undefined;
                    
                    return (
                      <Card key={index} className="p-3 sm:p-4">
                        {isExisting && !isExpanded ? (
                          // Collapsed view for existing payments
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                                <span className="font-medium text-sm">Payment #{index + 1}</span>
                                <span className="text-xs sm:text-sm text-gray-600">{new Date(payment.date).toLocaleDateString('en-GB')}</span>
                                <span className="font-semibold text-sm">{payment.currency} {payment.amount.toLocaleString()}</span>
                                {payment.invoice_currency_equivalent && payment.currency !== currency && (
                                  <span className="text-xs text-green-700 font-medium">({currency} {payment.invoice_currency_equivalent.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})})</span>
                                )}
                                <span className="text-xs text-gray-500 truncate">via {payment.account}</span>
                                {payment.payment_type === 'GST' && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">🇮🇳 GST</span>
                                )}
                                {payment.payment_type === 'ADDITIONAL' && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold">📦 Additional</span>
                                )}
                                {payment.proof_verified && (
                                  <span className="text-green-600 text-xs">✓ Verified</span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button 
                                onClick={() => setExpandedPayments(prev => {
                                  const newSet = new Set(prev);
                                  newSet.add(index);
                                  return newSet;
                                })} 
                                variant="secondary" 
                                size="sm"
                                className="text-xs"
                              >
                                Edit
                              </Button>
                              <Button 
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to delete this payment?')) {
                                    removePayment(index);
                                  }
                                }} 
                                variant="secondary" 
                                size="sm"
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // Expanded view for new payments or when editing
                          <div>
                            <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-2">
                              <h4 className="font-medium text-gray-900 text-sm sm:text-base">Payment #{index + 1}</h4>
                              <div className="flex gap-2">
                                {isExisting && (
                                  <Button 
                                    onClick={() => setExpandedPayments(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(index);
                                      return newSet;
                                    })} 
                                    variant="secondary" 
                                    size="sm"
                                    className="text-xs"
                                  >
                                    Collapse
                                  </Button>
                                )}
                                <Button 
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this payment?')) {
                                      removePayment(index);
                                    }
                                  }} 
                                  variant="secondary" 
                                  size="sm"
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                              <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                                <Input
                                  type="date"
                                  value={payment.date}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePayment(index, 'date', e.target.value)}
                                  className="w-full text-sm"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Amount *</label>
                                <Input
                                  type="number"
                                  placeholder="0.00"
                                  value={payment.amount}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePayment(index, 'amount', parseFloat(e.target.value) || 0)}
                                  className="w-full text-sm"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Currency *</label>
                                <Select 
                                  value={payment.currency} 
                                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updatePayment(index, 'currency', e.target.value)}
                                  className="w-full text-sm"
                                >
                                  {CURRENCY_OPTIONS.map(curr => (
                                    <option key={curr} value={curr}>{curr}</option>
                                  ))}
                                </Select>
                              </div>
                              
                              <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Exchange Rate to IDR *</label>
                                <Input
                                  type="number"
                                  placeholder="1.00"
                                  value={payment.exchange_rate}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePayment(index, 'exchange_rate', parseFloat(e.target.value) || 1)}
                                  className="w-full text-sm"
                                />
                              </div>
                              
                              {/* INR Conversion Info Box */}
                              {payment.currency === 'INR' && currency === 'USD' && (
                                <div className="sm:col-span-2 lg:col-span-3">
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-blue-600 font-medium text-sm">💱 INR → USD Conversion</span>
                                        {loadingInrRate && <span className="text-xs text-blue-400">Fetching rate...</span>}
                                      </div>
                                      {payment.applied_rate && rateOverrideIndex !== index && (
                                        <button
                                          onClick={() => { setRateOverrideIndex(index); setRateOverrideValue(String(payment.applied_rate || '')); }}
                                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                                        >
                                          Override rate
                                        </button>
                                      )}
                                    </div>
                                    {payment.applied_rate ? (
                                      <>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                          <div>
                                            <span className="text-gray-500">XE Rate:</span>
                                            <div className="font-medium">1 USD = {payment.xe_rate?.toFixed(2)} INR</div>
                                          </div>
                                          <div>
                                            <span className="text-gray-500">Markup:</span>
                                            <div className="font-medium">+{payment.rate_markup} INR</div>
                                          </div>
                                          <div>
                                            <span className="text-gray-500">Applied Rate:</span>
                                            <div className="font-semibold text-blue-700">1 USD = {payment.applied_rate?.toFixed(2)} INR</div>
                                          </div>
                                          <div>
                                            <span className="text-gray-500">USD Equivalent:</span>
                                            <div className="font-semibold text-green-700">
                                              {payment.invoice_currency_equivalent 
                                                ? `USD ${payment.invoice_currency_equivalent.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                                                : 'Enter amount'}
                                            </div>
                                          </div>
                                        </div>
                                        {/* Override input */}
                                        {rateOverrideIndex === index && (
                                          <div className="mt-2 pt-2 border-t border-blue-200 flex items-center gap-2">
                                            <span className="text-xs text-gray-600">Override applied rate:</span>
                                            <span className="text-xs">1 USD =</span>
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={rateOverrideValue}
                                              onChange={(e) => setRateOverrideValue(e.target.value)}
                                              className="w-24 px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            />
                                            <span className="text-xs">INR</span>
                                            <button
                                              onClick={() => {
                                                const newRate = parseFloat(rateOverrideValue);
                                                if (!newRate || newRate <= 0) return;
                                                const updated = [...payments];
                                                updated[index] = {
                                                  ...updated[index],
                                                  applied_rate: newRate,
                                                  rate_markup: Math.round((newRate - (updated[index].xe_rate || 0)) * 100) / 100,
                                                };
                                                if (updated[index].amount > 0) {
                                                  updated[index].invoice_currency_equivalent =
                                                    Math.round((updated[index].amount / newRate) * 100) / 100;
                                                }
                                                setPayments(updated);
                                                setRateOverrideIndex(null);
                                                setRateOverrideValue('');
                                              }}
                                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                            >
                                              Apply
                                            </button>
                                            <button
                                              onClick={() => { setRateOverrideIndex(null); setRateOverrideValue(''); }}
                                              className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded hover:bg-gray-300"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="text-xs text-gray-500">Select INR currency to fetch live rate</div>
                                    )}
                                    {payment.applied_rate && paymentSummary && paymentSummary.balance_due > 0 && (
                                      <div className="mt-2 pt-2 border-t border-blue-200 text-xs">
                                        <span className="text-gray-500">Remaining balance in INR: </span>
                                        <span className="font-semibold text-blue-700">
                                          INR {(paymentSummary.balance_due * payment.applied_rate).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </span>
                                        <span className="text-gray-400 ml-1">
                                          ({currency} {paymentSummary.balance_due.toLocaleString()} × {payment.applied_rate?.toFixed(2)})
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                                <Select 
                                  value={payment.account} 
                                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updatePayment(index, 'account', e.target.value)}
                                  className="w-full text-sm"
                                >
                                  <option value="" disabled>Select Payment Method</option>
                                  {PAYMENT_METHOD_OPTIONS.map(method => (
                                    <option key={method} value={method}>{method}</option>
                                  ))}
                                </Select>
                              </div>
                              
                              <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                <Input
                                  placeholder="Payment notes..."
                                  value={payment.remarks}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePayment(index, 'remarks', e.target.value)}
                                  className="w-full text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Payment Type *</label>
                                <Select
                                  value={payment.payment_type || 'DIRECT'}
                                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updatePayment(index, 'payment_type', e.target.value)}
                                  className={`w-full text-sm ${payment.payment_type === 'GST' ? 'border-orange-300 bg-orange-50' : payment.payment_type === 'ADDITIONAL' ? 'border-purple-300 bg-purple-50' : ''}`}
                                >
                                  <option value="DIRECT">Direct (to IIW)</option>
                                  <option value="GST">🇮🇳 GST (via India)</option>
                                  <option value="ADDITIONAL">📦 Additional (on-site extra)</option>
                                </Select>
                              </div>
                            </div>
                            
                            {/* Payment Proof Upload Section - Only for saved payments */}
                            {payment.id && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="mb-3">
                                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Payment Proof</label>
                                  
                                  {payment.proof_url ? (
                                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center gap-2 flex-1">
                                      <span className="text-green-600">📎</span>
                                      <span className="text-sm text-green-800 font-medium">Proof uploaded</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={async () => {
                                          try {
                                            const token = sessionStorage.getItem('jwt_token');
                                            // Extract S3 key from proof_url (handles both s3:// and https:// formats)
                                            const rawUrl = payment.proof_url || '';
                                            const s3Key = rawUrl.startsWith('s3://')
                                              ? rawUrl.replace(/^s3:\/\/[^/]+\//, '')
                                              : rawUrl.replace('https://wynder-v2.s3.amazonaws.com/', '');
                                            if (!s3Key || s3Key === rawUrl) {
                                              alert('Invalid proof URL');
                                              return;
                                            }
                                            console.log('Fetching presigned URL for S3 key:', s3Key);
                                            const response = await fetch(`/api/files/presigned-url/${encodeURIComponent(s3Key)}`, {
                                              headers: { 'Authorization': `Bearer ${token}` }
                                            });
                                            const data = await response.json();
                                            console.log('Presigned URL response:', data);
                                            if (data.success && data.data?.url) {
                                              const fileName = payment.proof_url?.split('/').pop() || 'payment_proof';
                                              const fileExt = fileName.toLowerCase().split('.').pop();
                                              const fileType = ['jpg', 'jpeg', 'png', 'gif'].includes(fileExt || '') ? 'image' : 'pdf';
                                              setViewerState({ isOpen: true, fileUrl: data.data.url, fileName, fileType });
                                            } else {
                                              alert('Unable to load file preview');
                                            }
                                          } catch (error) {
                                            console.error('Error getting presigned URL:', error);
                                            alert('Error loading file preview');
                                          }
                                        }}
                                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                      >
                                        👁️ View
                                      </button>
                                      <button
                                        onClick={() => deletePaymentProof(index)}
                                        className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                      >
                                        🗑️ Delete
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-3 border-2 border-dashed border-gray-300 rounded-lg">
                                    <input
                                      type="file"
                                      accept=".jpg,.jpeg,.png,.pdf"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          uploadPaymentProof(index, file);
                                          e.target.value = '';
                                        }
                                      }}
                                      disabled={uploading[index]}
                                      className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                                    />
                                    {uploading[index] && (
                                      <div className="flex items-center gap-2 text-blue-600 mt-2">
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                        <span className="text-xs">Uploading...</span>
                                      </div>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">Upload payment proof (JPG, PNG, PDF - Max 5MB)</p>
                                  </div>
                                )}
                              </div>
                            </div>
                            )}
                            
                            {!payment.id && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="text-sm text-orange-600 bg-orange-50 p-3 rounded">
                                  💡 Save payment first, then upload proof file
                                </div>
                              </div>
                            )}
                            
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                                  <div>
                                    <strong>IDR Equivalent:</strong> {(payment.amount * payment.exchange_rate).toLocaleString()} IDR
                                  </div>
                                  {payment.invoice_currency_equivalent && payment.currency !== currency && (
                                    <div className="text-green-700 font-medium">
                                      <strong>Invoice Equivalent:</strong> {currency} {payment.invoice_currency_equivalent.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </div>
                                  )}
                                </div>
                                {payment.id && (
                                  <Button 
                                    size="sm" 
                                    variant="secondary"
                                    onClick={() => {
                                      const fileUrl = payment.proof_url;
                                      if (fileUrl) {
                                        window.open(fileUrl, '_blank');
                                      } else {
                                        alert('No proof file available');
                                      }
                                    }}
                                    className="text-xs"
                                    disabled={!payment.proof_url}
                                  >
                                    {payment.proof_url ? "View Proof" : "No Proof"}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div className="text-xs sm:text-sm text-gray-600">
                  Total Payments: <strong>{currency} {payments.reduce((sum, p) => {
                    const payCur = p.payment_currency || p.currency;
                    if (payCur === currency) return sum + p.amount;
                    if (p.invoice_currency_equivalent && p.invoice_currency_equivalent > 0) return sum + p.invoice_currency_equivalent;
                    return sum;
                  }, 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                </div>
                <Button onClick={savePayments} disabled={saving} variant="primary" size="sm" className="text-xs sm:text-sm">
                  {saving ? 'Saving...' : '💾 Save All Payments'}
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'gst' && (
            <div>
              {gstLoading ? (
                <div className="text-center py-8">Loading GST data...</div>
              ) : (
                <div>
                  {/* Existing GST Invoice Banner */}
                  {gstExisting && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={`text-xs px-2 py-0.5 rounded-full mr-2 ${gstDocumentType === 'PROFORMA' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                            {gstDocumentType === 'PROFORMA' ? 'PROFORMA' : 'TAX INVOICE'}
                          </span>
                          <span className="font-medium text-green-800">{gstExisting.gst_invoice_number}</span>
                          <span className="text-xs text-green-600 ml-2">({gstExisting.financial_year})</span>
                        </div>
                        <span className="text-sm font-semibold text-green-700">₹{gstExisting.total_amount?.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                      </div>
                      {gstDocumentType === 'PROFORMA' && (
                        <div className="mt-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-1.5">
                          ⚠️ This is a Proforma Invoice. Exchange rate is indicative. Convert to Tax Invoice after receiving payment.
                        </div>
                      )}
                      {gstExisting.gst_needs_review && (
                        <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5">
                          🚨 <strong>GST Invoice review needed:</strong> Generated for WH_{gstExisting.gst_created_for_itinerary}. Please review and regenerate for the current itinerary.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rate Comparison — show when tax invoice has different rate from proforma */}
                  {gstExisting && gstExisting.proforma_applied_rate && gstDocumentType === 'TAX_INVOICE' && gstExisting.proforma_applied_rate !== gstExisting.applied_rate && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                      <div className="font-medium text-amber-800 mb-2">💱 Exchange Rate Comparison</div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-gray-500">Proforma ({gstExisting.proforma_date ? new Date(gstExisting.proforma_date).toLocaleDateString('en-GB') : ''})</div>
                          <div>XE {gstExisting.proforma_xe_rate?.toFixed(2)} + ₹1 = <span className="font-medium">{gstExisting.proforma_applied_rate?.toFixed(2)}</span></div>
                          <div>Package: <span className="font-medium">₹{gstExisting.proforma_value_inr?.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span></div>
                        </div>
                        <div>
                          <div className="text-gray-500">Tax Invoice ({gstExisting.invoice_date ? new Date(gstExisting.invoice_date).toLocaleDateString('en-GB') : ''})</div>
                          <div>XE {gstExisting.xe_rate?.toFixed(2)} + ₹1 = <span className="font-medium">{gstExisting.applied_rate?.toFixed(2)}</span></div>
                          <div>Package: <span className="font-medium">₹{gstExisting.booking_value_inr?.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span></div>
                        </div>
                      </div>
                      {(() => {
                        const diff = (gstExisting.booking_value_inr || 0) - (gstExisting.proforma_value_inr || 0);
                        const pct = gstExisting.proforma_value_inr ? ((diff / gstExisting.proforma_value_inr) * 100).toFixed(2) : '0';
                        return diff !== 0 ? (
                          <div className={`mt-2 font-medium ${diff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            Difference: {diff > 0 ? '+' : ''}₹{diff.toLocaleString('en-IN', {minimumFractionDigits: 2})} ({diff > 0 ? '+' : ''}{pct}%)
                          </div>
                        ) : (
                          <div className="mt-2 text-green-600 font-medium">No rate difference ✅</div>
                        );
                      })()}
                    </div>
                  )}

                  {gstData && (
                    <>
                      {/* Conversion Info with Override - only show when invoice is not already INR */}
                      {currency !== 'INR' ? (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-blue-700">💱 {currency} → INR Conversion</span>
                            {gstRateOverride === null && (
                              <button onClick={() => setGstRateOverride(gstData.applied_rate)} className="text-xs text-blue-600 hover:text-blue-800 underline">Override rate</button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <div><span className="text-gray-500">Booking ({currency}):</span><div className="font-medium">{currency === 'USD' ? '$' : currency} {gstData.booking_value_usd?.toLocaleString()}</div></div>
                            <div><span className="text-gray-500">XE Rate:</span><div className="font-medium">{gstData.xe_rate?.toFixed(2)}</div></div>
                            <div>
                              <span className="text-gray-500">Applied Rate (+{(gstData.applied_rate - gstData.xe_rate).toFixed(2)}):</span>
                              {gstRateOverride !== null ? (
                                <div className="flex items-center gap-1 mt-1">
                                  <input
                                    type="number"
                                    value={gstRateOverride}
                                    onChange={(e) => setGstRateOverride(parseFloat(e.target.value) || 0)}
                                    className="w-20 px-1 py-0.5 text-xs border border-blue-300 rounded"
                                  />
                                  <button onClick={() => {
                                    const newInr = Math.round(gstData.booking_value_usd * gstRateOverride * 100) / 100;
                                    const afterDiscount = newInr - gstDiscount;
                                    const newHandling = calculateHandlingCharge(afterDiscount);
                                    setGstHandlingCharge(newHandling);
                                    setGstData({...gstData, applied_rate: gstRateOverride, booking_value_inr: newInr, handling_charge: newHandling});
                                    setGstRateOverride(null);
                                  }} className="px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded">Apply</button>
                                  <button onClick={() => setGstRateOverride(null)} className="px-1.5 py-0.5 text-xs bg-gray-200 rounded">Cancel</button>
                                </div>
                              ) : (
                                <div className="font-medium">{gstData.applied_rate?.toFixed(2)}</div>
                              )}
                            </div>
                            <div>
                              <span className="text-gray-500">Booking (INR):</span>
                              <div className="font-semibold flex items-center gap-1">
                                <span>₹{gstData.booking_value_inr?.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                                {gstBookingValueOverride === null && (
                                  <button onClick={() => setGstBookingValueOverride(gstData.booking_value_inr)} className="text-xs text-blue-600 hover:text-blue-800 underline font-normal">Override</button>
                                )}
                              </div>
                              {gstBookingValueOverride !== null && (
                                <div className="mt-1 flex items-center gap-1">
                                  <span>₹</span>
                                  <input type="number" value={gstBookingValueOverride} onChange={(e) => setGstBookingValueOverride(parseFloat(e.target.value) || 0)} className="w-24 px-1 py-0.5 text-xs border border-blue-300 rounded" />
                                  <button onClick={() => {
                                    const newHandling = calculateHandlingCharge(gstBookingValueOverride - gstDiscount);
                                    setGstHandlingCharge(newHandling);
                                    setGstData({...gstData, booking_value_inr: gstBookingValueOverride, handling_charge: newHandling});
                                    setGstBookingValueOverride(null);
                                  }} className="px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded">Apply</button>
                                  <button onClick={() => setGstBookingValueOverride(null)} className="px-1.5 py-0.5 text-xs bg-gray-200 rounded">Cancel</button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-xs">
                          <span className="font-medium text-green-700">🇮🇳 Invoice already in INR — no conversion needed</span>
                          <div className="mt-1 font-semibold flex items-center gap-2">
                            <span>Booking Value: ₹{(gstData.booking_value_inr || paymentSummary?.invoice_total || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                            {gstBookingValueOverride === null && (
                              <button onClick={() => setGstBookingValueOverride(gstData.booking_value_inr || paymentSummary?.invoice_total || 0)} className="text-xs text-green-600 hover:text-green-800 underline">Override</button>
                            )}
                          </div>
                          {gstBookingValueOverride !== null && (
                            <div className="mt-2 flex items-center gap-1">
                              <span>₹</span>
                              <input
                                type="number"
                                value={gstBookingValueOverride}
                                onChange={(e) => setGstBookingValueOverride(parseFloat(e.target.value) || 0)}
                                className="w-32 px-2 py-1 text-xs border border-green-300 rounded"
                              />
                              <button onClick={() => {
                                const newHandling = calculateHandlingCharge(gstBookingValueOverride - gstDiscount);
                                setGstHandlingCharge(newHandling);
                                setGstData({...gstData, booking_value_inr: gstBookingValueOverride, booking_value_usd: gstBookingValueOverride, handling_charge: newHandling});
                                setGstBookingValueOverride(null);
                              }} className="px-2 py-1 text-xs bg-green-600 text-white rounded">Apply</button>
                              <button onClick={() => setGstBookingValueOverride(null)} className="px-2 py-1 text-xs bg-gray-200 rounded">Cancel</button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Recalculate from current invoice */}
                      <div className="mb-4 flex justify-end">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            const invoiceTotal = paymentSummary?.invoice_total || invoice.total_amount || 0;
                            const bookingInr = currency === 'INR' ? invoiceTotal : (gstData.booking_value_inr || invoiceTotal);
                            const newHandling = calculateHandlingCharge(bookingInr - gstDiscount);
                            setGstHandlingCharge(newHandling);
                            setGstData({
                              ...gstData,
                              booking_value_inr: bookingInr,
                              booking_value_usd: currency === 'INR' ? invoiceTotal : gstData.booking_value_usd,
                              handling_charge: newHandling,
                            });
                          }}
                          className="text-xs"
                        >
                          🔄 Recalculate from Invoice
                        </Button>
                      </div>

                      {/* Booking Description */}
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Booking Package Description</label>
                        <Input
                          placeholder="e.g. Bali Package (Reimbursement to DMC)"
                          value={gstBookingDescription}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGstBookingDescription(e.target.value)}
                          className="w-full text-sm"
                        />
                      </div>

                      {/* Form */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Date</label>
                          <Input
                            type="date"
                            value={gstInvoiceDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGstInvoiceDate(e.target.value)}
                            className="w-full text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Handling Charges (₹)</label>
                          {gstHandlingOverride !== null ? (
                            <div className="flex items-center gap-1">
                              <span className="text-sm">₹</span>
                              <Input
                                type="number"
                                value={gstHandlingOverride}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGstHandlingOverride(parseFloat(e.target.value) || 0)}
                                className="w-full text-sm"
                              />
                              <button onClick={() => {
                                setGstHandlingCharge(gstHandlingOverride);
                                setGstHandlingOverride(null);
                              }} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Apply</button>
                              <button onClick={() => setGstHandlingOverride(null)} className="px-2 py-1 text-xs bg-gray-200 rounded">Cancel</button>
                            </div>
                          ) : (
                            <div className="p-2 bg-gray-100 rounded text-sm font-medium flex items-center justify-between">
                              <span>₹{(gstHandlingCharge || calculateHandlingCharge((gstData?.booking_value_inr || 0) - gstDiscount)).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                              <button onClick={() => setGstHandlingOverride(gstHandlingCharge || calculateHandlingCharge((gstData?.booking_value_inr || 0) - gstDiscount))} className="text-xs text-blue-600 hover:text-blue-800 underline">Override</button>
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            Slab: &lt;50k→₹500 | 50k-1L→₹1,000 | 1L-1.5L→₹2,000 | &gt;1.5L→₹3,000
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Recalculate</label>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => recalculateGst()}
                            className="text-xs whitespace-nowrap"
                          >
                            Recalculate
                          </Button>
                        </div>
                      </div>

                      {/* Additional Items */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-medium text-gray-700">Additional Service Fees</label>
                          <button
                            onClick={() => setGstAdditionalItems([...gstAdditionalItems, {description: '', amount: 0}])}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            + Add Item
                          </button>
                        </div>
                        {gstAdditionalItems.map((item, i) => (
                          <div key={i} className="flex gap-2 mb-2">
                            <Input
                              placeholder="Description (e.g. Visa Assistance)"
                              value={item.description}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updated = [...gstAdditionalItems];
                                updated[i] = {...updated[i], description: e.target.value};
                                setGstAdditionalItems(updated);
                              }}
                              className="flex-1 text-sm"
                            />
                            <Input
                              type="number"
                              placeholder="Amount"
                              value={item.amount}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const updated = [...gstAdditionalItems];
                                updated[i] = {...updated[i], amount: parseFloat(e.target.value) || 0};
                                setGstAdditionalItems(updated);
                              }}
                              className="w-32 text-sm"
                            />
                            <button
                              onClick={() => setGstAdditionalItems(gstAdditionalItems.filter((_, idx) => idx !== i))}
                              className="text-red-500 hover:text-red-700 text-xs px-2"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Discount */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Discount (₹)</label>
                          <Input
                            type="number"
                            value={gstDiscount}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGstDiscount(parseFloat(e.target.value) || 0)}
                            className="w-full text-sm"
                          />
                        </div>
                      </div>

                      {/* Buyer GST Details */}
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <h4 className="text-xs font-medium text-gray-700 mb-2">Agent GST Details</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">GSTIN</label>
                            <Input
                              placeholder="e.g. 32AABCU9603R1ZM"
                              value={gstBuyerGstin}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const val = e.target.value.toUpperCase();
                                setGstBuyerGstin(val);
                                // Auto-extract state code from first 2 digits of GSTIN
                                if (val.length >= 2) {
                                  const code = val.substring(0, 2);
                                  const stateMap: {[key: string]: string} = {
                                    '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
                                    '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana',
                                    '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh',
                                    '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
                                    '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
                                    '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam',
                                    '19': 'West Bengal', '20': 'Jharkhand', '21': 'Odisha',
                                    '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
                                    '26': 'Dadra & Nagar Haveli', '27': 'Maharashtra', '29': 'Karnataka',
                                    '30': 'Goa', '31': 'Lakshadweep', '32': 'Kerala',
                                    '33': 'Tamil Nadu', '34': 'Puducherry', '35': 'Andaman & Nicobar',
                                    '36': 'Telangana', '37': 'Andhra Pradesh', '38': 'Ladakh',
                                  };
                                  if (stateMap[code]) {
                                    setGstBuyerStateCode(code);
                                    setGstBuyerState(stateMap[code]);
                                  }
                                }
                              }}
                              className="w-full text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">State</label>
                            <Input
                              placeholder="e.g. Kerala"
                              value={gstBuyerState}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGstBuyerState(e.target.value)}
                              className="w-full text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">State Code</label>
                            <Input
                              placeholder="e.g. 32"
                              value={gstBuyerStateCode}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGstBuyerStateCode(e.target.value)}
                              className="w-full text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">GST Type</label>
                            <Select
                              value={gstBuyerStateCode && gstBuyerStateCode === gstData?.supplier?.state_code ? 'CGST_SGST' : 'IGST'}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                if (e.target.value === 'CGST_SGST') {
                                  // Set buyer state code same as supplier to force intra-state
                                  setGstBuyerStateCode(gstData?.supplier?.state_code || '');
                                  setGstBuyerState(gstData?.supplier?.state || '');
                                } else {
                                  // Clear state code to force inter-state (if it was same as supplier)
                                  if (gstBuyerStateCode === gstData?.supplier?.state_code) {
                                    setGstBuyerStateCode('');
                                    setGstBuyerState('');
                                  }
                                }
                              }}
                              className="w-full text-sm"
                            >
                              <option value="IGST">IGST (Inter-State)</option>
                              <option value="CGST_SGST">CGST + SGST (Intra-State)</option>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* GST Calculation Summary */}
                      <div className="mb-4 p-4 bg-white border border-gray-300 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3">GST Calculation</h4>
                        {(() => {
                          const additionalTotal = gstAdditionalItems.reduce((sum, item) => sum + (item.amount || 0), 0);
                          const bookingValueInr = gstData.booking_value_inr || 0;
                          const handlingCharge = gstHandlingCharge || calculateHandlingCharge(bookingValueInr - gstDiscount);
                          const packageCost = bookingValueInr - gstDiscount - handlingCharge;
                          const taxableValue = handlingCharge + additionalTotal;
                          const gstType = gstBuyerStateCode && gstBuyerStateCode === gstData.supplier?.state_code ? 'CGST_SGST' : 'IGST';
                          const gstRate = gstData.gst_rate || 18;
                          const igst = gstType === 'IGST' ? Math.round(taxableValue * gstRate / 100 * 100) / 100 : 0;
                          const cgst = gstType === 'CGST_SGST' ? Math.round(taxableValue * gstRate / 2 / 100 * 100) / 100 : 0;
                          const sgst = cgst;
                          const total = Math.round((taxableValue + igst + cgst + sgst) * 100) / 100;

                          return (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Booking Value (INR)</span>
                                <span>₹{bookingValueInr.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                              </div>
                              {gstDiscount > 0 && (
                                <div className="flex justify-between text-red-600">
                                  <span>Discount</span>
                                  <span>-₹{gstDiscount.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-gray-600">Handling Charges (deducted)</span>
                                <span>₹{handlingCharge.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                              </div>
                              <div className="flex justify-between text-gray-500">
                                <span>Reimbursement to DMC</span>
                                <span>₹{packageCost.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                              </div>
                              {additionalTotal > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Additional Fees</span>
                                  <span>₹{additionalTotal.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-medium border-t pt-2">
                                <span>Taxable Value (Handling Charges)</span>
                                <span>₹{taxableValue.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                              </div>
                              {gstType === 'IGST' ? (
                                <div className="flex justify-between text-blue-700">
                                  <span>IGST @ {gstRate}%</span>
                                  <span>₹{igst.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                                </div>
                              ) : (
                                <>
                                  <div className="flex justify-between text-blue-700">
                                    <span>CGST @ {gstRate/2}%</span>
                                    <span>₹{cgst.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                                  </div>
                                  <div className="flex justify-between text-blue-700">
                                    <span>SGST @ {gstRate/2}%</span>
                                    <span>₹{sgst.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                                  </div>
                                </>
                              )}
                              <div className="flex justify-between font-bold text-lg border-t pt-2">
                                <span>Total (Handling + GST)</span>
                                <span>₹{total.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                              </div>
                              <div className="flex justify-between font-bold text-lg">
                                <span>Grand Total (incl. Package)</span>
                                <span>₹{(packageCost + total).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                GST Type: {gstType === 'IGST' ? 'Inter-State (IGST)' : 'Intra-State (CGST + SGST)'}
                                {' | SAC: '}{gstData.sac_code}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Notes */}
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <Input
                          placeholder="Additional notes..."
                          value={gstNotes}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGstNotes(e.target.value)}
                          className="w-full text-sm"
                        />
                      </div>

                      {/* Save & Print Buttons */}
                      <div className="flex flex-wrap gap-3">
                        <Button onClick={saveGstInvoice} disabled={gstSaving} className="text-sm">
                          {gstSaving ? 'Saving...' : !gstExisting ? '💾 Save as Proforma' : gstDocumentType === 'PROFORMA' ? '💾 Update Proforma' : '💾 Update Tax Invoice'}
                        </Button>
                        {gstExisting && gstDocumentType === 'PROFORMA' && (
                          <Button
                            variant="primary"
                            className="text-sm bg-green-600 hover:bg-green-700"
                            onClick={async () => {
                              if (!window.confirm('Convert to Tax Invoice? This will recalculate at current XE rate and lock the invoice.')) return;
                              setGstDocumentType('TAX_INVOICE');
                              // Recalculate at current rate
                              await recalculateGst();
                              // Save with TAX_INVOICE type
                              setGstSaving(true);
                              try {
                                const token = sessionStorage.getItem('jwt_token');
                                const res = await fetch(`/api/gst-invoice/${invoice.booking_id}/save`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                  body: JSON.stringify({
                                    handling_charge: gstHandlingCharge,
                                    booking_value_inr_override: gstData?.booking_value_inr,
                                    xe_rate_override: gstData?.xe_rate,
                                    applied_rate_override: gstData?.applied_rate,
                                    additional_items: gstAdditionalItems,
                                    discount: gstDiscount,
                                    notes: gstNotes,
                                    booking_description: gstBookingDescription,
                                    buyer_gstin: gstBuyerGstin,
                                    buyer_state: gstBuyerState,
                                    buyer_state_code: gstBuyerStateCode,
                                    invoice_date: new Date().toISOString().split('T')[0],
                                    document_type: 'TAX_INVOICE',
                                  }),
                                });
                                const data = await res.json();
                                if (data.success) {
                                  alert(`Tax Invoice ${data.gst_invoice_number} generated!`);
                                  loadGstData();
                                } else { alert('Error: ' + (data.detail || data.message)); }
                              } catch (e) { alert('Error converting to tax invoice'); }
                              finally { setGstSaving(false); }
                            }}
                          >
                            ✅ Convert to Tax Invoice
                          </Button>
                        )}
                        {gstExisting && (
                          <Button
                            variant="secondary"
                            className="text-sm"
                            onClick={async () => {
                              try {
                                const token = sessionStorage.getItem('jwt_token');
                                const response = await fetch(`/api/gst-invoice/${invoice.booking_id}/print`, {
                                  headers: { 'Authorization': `Bearer ${token}` },
                                });
                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  const prefix = gstDocumentType === 'PROFORMA' ? 'Proforma' : 'GST_Invoice';
                                  a.download = `${prefix}_${invoice.itinerary_id}.docx`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } else {
                                  alert('Error generating invoice');
                                }
                              } catch (error) {
                                console.error('Error printing:', error);
                                alert('Error generating invoice');
                              }
                            }}
                          >
                            🖨️ Print {gstDocumentType === 'PROFORMA' ? 'Proforma' : 'Tax Invoice'}
                          </Button>
                        )}
                      </div>
                    </>
                  )}

                  {!gstData && !gstLoading && (
                    <div className="text-center py-8">
                      <Button onClick={loadGstData}>Load GST Data</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'sub_invoices' && (
            <SubInvoicesTab bookingId={invoice.booking_id} />
          )}

          {activeTab === 'unified' && (
            <UnifiedViewTab bookingId={invoice.booking_id} currency={currency} payments={payments} paymentSummary={paymentSummary} />
          )}

          {activeTab === 'reconciliation' && paymentSummary && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-4">Payment Reconciliation</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Invoice Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>{currency} {(paymentSummary.invoice_total + discount).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Discount:</span>
                        <span>-{currency} {discount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2">
                        <span>Total:</span>
                        <span>{currency} {paymentSummary.invoice_total.toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Payment Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Total Received:</span>
                        <div className="text-right">
                          <div>{currency} {paymentSummary.payments_total.toLocaleString()}</div>
                          {currency !== 'IDR' && (
                            <div className="text-xs text-gray-500">
                              IDR {(paymentSummary.payments_total * (payments.length > 0 ? payments[0].exchange_rate : 1)).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span>Balance Due:</span>
                        <div className="text-right">
                          <div className={paymentSummary.balance_due > 0 ? 'text-red-600' : 'text-green-600'}>
                            {currency} {paymentSummary.balance_due.toLocaleString()}
                          </div>
                          {currency !== 'IDR' && paymentSummary.balance_due > 0 && (
                            <div className="text-xs text-gray-500">
                              IDR {(paymentSummary.balance_due * (payments.length > 0 ? payments[0].exchange_rate : 1)).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-2">
                        <span>Status:</span>
                        <span className={`${paymentSummary.payment_status === 'FULLY_PAID' ? 'text-green-600' : paymentSummary.payment_status === 'PARTIALLY_PAID' ? 'text-yellow-600' : 'text-red-600'}`}>
                          {paymentSummary.payment_status}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6">
                <h4 className="font-semibold mb-2">Payment History</h4>
                <div className="space-y-2">
                  {payments.filter(p => p.amount > 0).map((payment, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div>
                        <span className="font-medium">{new Date(payment.date).toLocaleDateString('en-GB')}</span> - {payment.account}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div>{payment.currency} {payment.amount.toLocaleString()}</div>
                          {payment.currency !== 'IDR' && (
                            <div className="text-xs text-gray-500">
                              IDR {(payment.amount * payment.exchange_rate).toLocaleString()}
                            </div>
                          )}
                        </div>
                        {payment.proof_url ? (
                          <span className="text-green-600 text-sm">✓ Proof Uploaded</span>
                        ) : (
                          <span className="text-gray-600 text-sm">No Proof</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <FileViewerModal
        isOpen={viewerState.isOpen}
        onClose={() => setViewerState({ ...viewerState, isOpen: false })}
        fileUrl={viewerState.fileUrl}
        fileName={viewerState.fileName}
        fileType={viewerState.fileType}
      />
    </div>
  );
};

export default InvoiceRevampModal;